import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import {
  useFetchAllRidersQuery,
  useUpdateOrderRiderMutation,
} from "../../store";
import { IoChevronDown, IoPersonOutline } from "react-icons/io5";
import { Rider } from "../../types";
import useDebounce from "../../hooks/useDebounce";
import Skeleton from "./Skeleton";
import { showToast, ToastType } from "../../utils/toast.utils";

interface RiderSelectProps {
  orderId: string;
  value?: { _id: string; fullName: string } | null;
  placeholder?: string;
  className?: string;
}

const RiderSelect: React.FC<RiderSelectProps> = ({
  orderId,
  value,
  placeholder,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [showAll, setShowAll] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const debouncedSearchTerm = useDebounce(searchText, 400);

  // Fetch riders
  const queryParam = showAll ? "" : debouncedSearchTerm;
  const { data: riders = [], isFetching: isFetchingRiders } =
    useFetchAllRidersQuery(queryParam, {
      skip: !showAll && debouncedSearchTerm.length < 2,
    });

  const [updateOrderRider, { isLoading: isUpdating }] =
    useUpdateOrderRiderMutation();

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
    if (!isOpen) setIsOpen(true);
    setShowAll(false);
  };

  const handleOptionClick = async (rider: Rider) => {
    setIsOpen(false);
    setSearchText("");
    setShowAll(false);

    if (value?._id === rider._id) {
      return;
    }

    const result = await updateOrderRider({ orderId, riderId: rider._id });
    if (!result.error) {
      showToast("Livreur mis à jour", ToastType.SUCCESS);
    }
  };

  const handleToggleDropdown = () => {
    if (!isOpen) {
      setShowAll(true);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
    setIsOpen(!isOpen);
  };

  // Close on outside click
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setShowAll(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Open dropdown if typing
  useEffect(() => {
    if (debouncedSearchTerm.length > 1) setIsOpen(true);
  }, [debouncedSearchTerm]);

  return (
    <div
      ref={containerRef}
      className={classNames("relative w-full max-w-[240px]", className)}
    >
      {/* Trigger Area */}
      <div
        className={classNames(
          "flex items-center gap-2 px-3 py-2 bg-white border rounded-lg transition-all cursor-text",
          isOpen
            ? "border-primary ring-2 ring-primary/20"
            : "border-gray-200 hover:border-gray-300"
        )}
        onClick={() => {
          if (!isOpen) handleToggleDropdown();
          inputRef.current?.focus();
        }}
      >
        <IoPersonOutline className="text-gray-400 w-4 h-4 flex-shrink-0" />

        <input
          ref={inputRef}
          value={isOpen || searchText ? searchText : value?.fullName || ""}
          placeholder={
            value?.fullName ? "" : placeholder || "Assigner un livreur"
          }
          className="w-full text-sm bg-transparent border-none outline-none text-gray-700 placeholder:text-gray-400 font-medium"
          onChange={handleOnChange}
          autoComplete="off"
          onFocus={() => {
             // Optional: auto-open on focus?
             // setIsOpen(true); 
          }}
        />

        {isUpdating && (
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin flex-shrink-0" />
        )}

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleToggleDropdown();
          }}
          className="p-1 hover:bg-gray-100 rounded-md transition-colors text-gray-400 hover:text-gray-600"
        >
          <IoChevronDown
            className={classNames("transition-transform duration-200", {
              "rotate-180": isOpen,
            })}
            size={16}
          />
        </button>
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-100 shadow-xl rounded-lg max-h-60 overflow-y-auto py-1 z-50 animate-in fade-in zoom-in-95 duration-100">
          {isFetchingRiders ? (
            <div className="p-2 space-y-2">
              <Skeleton className="h-8 w-full rounded" times={3} />
            </div>
          ) : riders && riders.length > 0 ? (
            riders.map((rider) => (
              <div
                key={rider._id}
                className="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm text-gray-700 transition-colors flex items-center gap-2"
                onClick={() => handleOptionClick(rider)}
              >
                <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-bold text-gray-500 flex-shrink-0">
                  {rider.fullName.charAt(0)}
                </div>
                <span className="truncate">{rider.fullName}</span>
              </div>
            ))
          ) : (
            <div className="px-4 py-3 text-sm text-gray-500 text-center italic">
              Aucun livreur trouvé
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RiderSelect;
